import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { BACKEND_BASE_URL } from '../constants';

interface ShareDialogProps {
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
  language?: string;
}

const ShareDialog: React.FC<ShareDialogProps> = ({ sessionId, isOpen, onClose, language = 'en' }) => {
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const { t } = useTranslation(language);

  React.useEffect(() => {
    if (isOpen && !shareUrl) {
      generateShareLink();
    }
  }, [isOpen]);

  const generateShareLink = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/api/share/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to generate share link');
      }

      const data = await response.json();
      setShareUrl(data.share_url);
    } catch (err) {
      console.error('Error generating share link:', err);
      setError(t('share.error') || 'Failed to generate share link');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wwx-share-dialog-overlay" onClick={onClose}>
      <div className="wwx-share-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="wwx-share-dialog-header">
          <h3>{t('share.title') || 'Share Conversation'}</h3>
          <button className="wwx-share-dialog-close" onClick={onClose}>×</button>
        </div>
        
        <div className="wwx-share-dialog-content">
          <p className="wwx-share-dialog-description">
            {t('share.description') || 'Share this conversation with your friends using the link below:'}
          </p>
          
          {isLoading && (
            <div className="wwx-share-dialog-loading">
              {t('share.generating') || 'Generating share link...'}
            </div>
          )}
          
          {error && (
            <div className="wwx-share-dialog-error">
              {error}
            </div>
          )}
          
          {shareUrl && !isLoading && (
            <div className="wwx-share-dialog-link-container">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="wwx-share-dialog-link-input"
                onClick={(e) => e.currentTarget.select()}
              />
              <button
                className="wwx-share-dialog-copy-button"
                onClick={copyToClipboard}
              >
                {isCopied ? (t('share.copied') || 'Copied!') : (t('share.copy') || 'Copy')}
              </button>
            </div>
          )}
          
          <div className="wwx-share-dialog-note">
            <small>
              {t('share.note') || 'Note: This link will expire in 30 days. When someone opens this link, they will see a copy of this conversation.'}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;